import React, { useState } from 'react';
import AncestrySelection from './AncestrySelection';
import CommunitySelection from './CommunitySelection';
import ClassSelection from './ClassSelection';
import SubclassSelection from './SubclassSelection';
import TraitsAssignment from './TraitsAssignment';
import StartingChoices from './StartingChoices';
import CharacterCustomization from './CharacterCustomization';
import ExperiencesSelection from './ExperiencesSelection';
import DomainCardsSelection from './DomainCardsSelection';
import StartingEquipment from './StartingEquipment';
import CharacterSummary from './CharacterSummary';
import ProgressSidePanel from './ProgressSidePanel';
import Card from '../Common/Card';
import Button from '../Common/Button';
import { validateTraitAssignments } from '../../utils/traitValidation';
import { canProceedWithExperiences } from '../../utils/experienceValidation';
import { ContentReference } from '../../types/domain-card';
import { BackgroundAnswer, ConnectionAnswer } from '../../types/character';
import styles from './CharacterCreationWizard.module.css';

export interface CharacterCreationData {
  ancestry: string;
  community: string;
  class: string;
  subclass?: string;
  name: string;

  // Phase 61: Trait assignments
  traits?: {
    agility: number;
    strength: number;
    finesse: number;
    instinct: number;
    presence: number;
    knowledge: number;
  };

  // Enhanced character creation data from class data
  startingChoices?: Record<string, string>; // category -> selected option
  appearance?: {
    clothes?: string;
    eyes?: string;
    body?: string;
    skin?: string;
    attitude?: string;
  };
  backgroundAnswers?: BackgroundAnswer[]; // {question, answer} pairs
  connectionAnswers?: ConnectionAnswer[]; // {prompt, answer} pairs

  // Equipment choices
  selectedEquipment?: {
    primaryWeapon?: boolean; // accept suggested primary weapon
    secondaryWeapon?: boolean; // accept suggested secondary weapon
    armor?: boolean; // accept suggested armor
  };

  // Phase 62: Experiences (exactly 2 required)
  experiences?: {
    id: string;
    name: string;
    bonus: number;      // Always 2 at creation
    createdAtLevel: number; // Always 1 at creation
  }[];

  // Phase 63: Domain Cards (exactly 2 cards required)
  domainCards?: ContentReference[];
}

interface CharacterCreationWizardProps {
  onComplete: (characterData: CharacterCreationData) => void;
  onCancel: () => void;
}

const STEPS = [
  { id: 'ancestry', title: 'Ancestry', component: AncestrySelection },
  { id: 'community', title: 'Community', component: CommunitySelection },
  { id: 'class', title: 'Class', component: ClassSelection },
  { id: 'subclass', title: 'Subclass', component: SubclassSelection },
  { id: 'traits', title: 'Traits', component: TraitsAssignment },
  { id: 'choices', title: 'Starting Choices', component: StartingChoices },
  { id: 'customization', title: 'Character Details', component: CharacterCustomization },
  { id: 'experiences', title: 'Experiences', component: ExperiencesSelection },
  { id: 'domainCards', title: 'Domain Cards', component: DomainCardsSelection },
  { id: 'equipment', title: 'Equipment', component: StartingEquipment },
  { id: 'summary', title: 'Summary', component: CharacterSummary },
];

const CharacterCreationWizard: React.FC<CharacterCreationWizardProps> = ({
  onComplete,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState(new Set([0])); // Track which steps have been visited
  const [characterData, setCharacterData] = useState<CharacterCreationData>({
    ancestry: '',
    community: '',
    class: '',
    subclass: '',
    name: '',
    traits: undefined,
    startingChoices: {},
    appearance: {},
    backgroundAnswers: [],
    connectionAnswers: [],
    selectedEquipment: { primaryWeapon: false, armor: false },
    experiences: [],
    domainCards: [],
  });

  const currentStepData = STEPS[currentStep];
  const StepComponent = currentStepData.component;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setVisitedSteps(prev => new Set(prev).add(nextStep));
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setVisitedSteps(prev => new Set(prev).add(prevStep));
    }
  };

  const handleStepData = (stepData: any) => {
    setCharacterData(prev => ({ ...prev, ...stepData }));
  };

  const handleComplete = () => {
    onComplete(characterData);
  };

  const handleStepNavigation = (stepIndex: number) => {
    setCurrentStep(stepIndex);
    setVisitedSteps(prev => new Set(prev).add(stepIndex));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Ancestry
        return !!characterData.ancestry;
      case 1: // Community
        return !!characterData.community;
      case 2: // Class
        return !!characterData.class;
      case 3: // Subclass
        return !!characterData.subclass;
      case 4: // Traits - REQUIRED
        return (
          characterData.traits &&
          Object.values(characterData.traits).every(v => v !== null) &&
          validateTraitAssignments(characterData.traits as any).isValid
        );
      case 5: // Starting Choices - optional
        return true;
      case 6: // Character Customization - optional
        return true;
      case 7: // Experiences - REQUIRED (exactly 2, both valid)
        return !!(
          characterData.experiences &&
          characterData.experiences.length === 2 &&
          canProceedWithExperiences(
            characterData.experiences[0]?.name || '',
            characterData.experiences[1]?.name || ''
          )
        );
      case 8: // Domain Cards - REQUIRED (exactly 2)
        return !!(characterData.domainCards && characterData.domainCards.length === 2);
      case 9: // Equipment - optional
        return true;
      case 10: // Summary
        return !!characterData.name.trim();
      default:
        return false;
    }
  };

  const isStepComplete = (stepIndex: number): boolean => {
    switch (stepIndex) {
      case 0: // Ancestry
        return !!characterData.ancestry;
      case 1: // Community
        return !!characterData.community;
      case 2: // Class
        return !!characterData.class;
      case 3: // Subclass
        return !!characterData.subclass;
      case 4: // Traits - REQUIRED
        return !!(
          characterData.traits &&
          validateTraitAssignments(characterData.traits as any).isValid
        );
      case 5: // Starting Choices - optional, only complete if visited and has data
        return visitedSteps.has(stepIndex);
      case 6: // Character Customization - optional, only complete if visited
        return visitedSteps.has(stepIndex);
      case 7: // Experiences - REQUIRED (exactly 2, both valid)
        return !!(
          characterData.experiences &&
          characterData.experiences.length === 2 &&
          canProceedWithExperiences(
            characterData.experiences[0]?.name || '',
            characterData.experiences[1]?.name || ''
          )
        );
      case 8: // Domain Cards - REQUIRED (exactly 2)
        return !!(characterData.domainCards && characterData.domainCards.length === 2);
      case 9: // Equipment - optional, only complete if visited
        return visitedSteps.has(stepIndex);
      case 10: // Summary
        return !!characterData.name.trim();
      default:
        return false;
    }
  };

  const isStepAccessible = (stepIndex: number): boolean => {
    switch (stepIndex) {
      case 0: // Ancestry - always accessible
      case 1: // Community - always accessible
      case 2: // Class - always accessible
        return true;
      case 3: // Subclass - requires class to be selected
      case 4: // Traits - requires class to be selected
      case 5: // Starting Choices - requires class to be selected
      case 6: // Character Customization - requires class to be selected
      case 7: // Experiences - requires class to be selected
      case 8: // Domain Cards - requires class to be selected
      case 9: // Equipment - requires class to be selected
        return !!characterData.class;
      case 10: // Summary - always accessible (but creation requires all required steps)
        return true;
      default:
        return false;
    }
  };

  const canCreateCharacter = (): boolean => {
    // Only required steps need to be complete for character creation
    return !!(
      !!characterData.ancestry &&
      !!characterData.community &&
      !!characterData.class &&
      !!characterData.subclass &&
      characterData.traits &&
      validateTraitAssignments(characterData.traits as any).isValid &&
      characterData.experiences &&
      characterData.experiences.length === 2 &&
      canProceedWithExperiences(
        characterData.experiences[0]?.name || '',
        characterData.experiences[1]?.name || ''
      ) &&
      characterData.domainCards &&
      characterData.domainCards.length === 2 &&
      !!characterData.name.trim()
    );
  };

  return (
    <div className={styles.characterCreationWizard}>
      <div className={styles.layout}>
        {/* Main Content */}
        <div className={styles.main}>
          <Card title="Create New Character">
            <div className={styles.content}>
              {/* Mobile Progress indicator */}
              <div className={`${styles.progress} ${styles.progressMobile}`}>
                <div className={styles.steps}>
                  {STEPS.map((step, index) => (
                    <div
                      key={step.id}
                      className={`${styles.step} ${
                        index === currentStep ? styles.stepActive : ''
                      } ${
                        isStepComplete(index) ? styles.stepCompleted : ''
                      } ${
                        !isStepAccessible(index) ? styles.stepDisabled : ''
                      }`}
                      onClick={() => isStepAccessible(index) && handleStepNavigation(index)}
                    >
                      <div className={styles.stepNumber}>
                        {isStepComplete(index) ? '✓' : index + 1}
                      </div>
                      <div className={styles.stepTitle}>{step.title}</div>
                    </div>
                  ))}
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Current step content */}
              <div className={styles.stepContent}>
                <h2>{currentStepData.title}</h2>
                <StepComponent
                  characterData={characterData}
                  onDataChange={handleStepData}
                />
              </div>

              {/* Navigation buttons */}
              <div className={styles.navigation}>
                <div className={styles.navLeft}>
                  <Button
                    variant="CLEAR"
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                  {!isFirstStep && (
                    <Button
                      variant="SECONDARY"
                      onClick={handlePrevious}
                    >
                      Previous
                    </Button>
                  )}
                </div>
                <div className={styles.navRight}>
                  {isLastStep ? (
                    <Button
                      variant="PRIMARY"
                      onClick={handleComplete}
                      disabled={!canCreateCharacter()}
                    >
                      Create Character
                    </Button>
                  ) : (
                    <Button
                      variant="PRIMARY"
                      onClick={handleNext}
                      disabled={!canProceed()}
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Side Panel - Desktop Only */}
        <div className={styles.sidebar}>
          <ProgressSidePanel 
            characterData={characterData}
            currentStep={currentStep}
            steps={STEPS.map(step => ({ id: step.id, title: step.title }))}
            visitedSteps={visitedSteps}
            onStepClick={handleStepNavigation}
          />
        </div>
      </div>
    </div>
  );
};

export default CharacterCreationWizard;